import React from "react";

import {
  ExternalLink,
  Folder,
  FolderOpen,
} from "../../../components/icons/svg/outline";
import { formatDateTime } from "../../../utils/datetime";
import ProjectSectionTitle from "./ProjectSectionTitle";
import { useProjectPageContext } from "./ProjectPageContext";

/**
 * Format an Egnyte epoch-ms timestamp for list display.
 *
 * @param {number|null|undefined} lastModified - Epoch milliseconds.
 * @returns {string|null}
 */
function formatFolderModified(lastModified) {
  if (lastModified == null || lastModified === "") {
    return null;
  }
  const date = new Date(Number(lastModified));
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return formatDateTime(date.toISOString(), "month DD, YYYY");
}

/**
 * One curated Egnyte folder row with open link and optional stats.
 *
 * @param {Object} props
 * @param {Object} props.folder - Folder row from document-folders state.
 * @param {boolean} props.statsLoading - Whether stats are still loading.
 * @returns {JSX.Element}
 */
function ProjectFileFolderRow({ folder, statsLoading }) {
  const modifiedLabel = formatFolderModified(folder.lastModified);
  const missing = folder.exists === false;
  const filesLabel =
    typeof folder.filesCount === "number"
      ? `${folder.filesCount} file${folder.filesCount === 1 ? "" : "s"}`
      : statsLoading
        ? "Loading…"
        : missing
          ? "Not created"
          : "—";

  const content = (
    <>
      <div className="flex min-w-0 items-center gap-2">
        {missing ? (
          <Folder className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden />
        ) : (
          <FolderOpen
            className="h-4 w-4 flex-shrink-0 text-primary"
            aria-hidden
          />
        )}
        <span
          className={`min-w-0 truncate text-sm font-semibold ${
            missing ? "text-gray-500" : "text-gray-900"
          }`}
        >
          {folder.name}
        </span>
      </div>
      <div className="flex flex-shrink-0 items-center gap-3 text-xs font-medium text-gray-500">
        <span className="tabular-nums">{filesLabel}</span>
        {modifiedLabel ? (
          <span className="hidden sm:inline">Updated {modifiedLabel}</span>
        ) : null}
        {!missing ? (
          <ExternalLink className="h-3.5 w-3.5 text-primary" aria-hidden />
        ) : null}
      </div>
    </>
  );

  if (missing || !folder.webUrl) {
    return (
      <li className="flex items-center justify-between gap-3 px-3 py-2.5">
        {content}
      </li>
    );
  }

  return (
    <li>
      <a
        href={folder.webUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between bg-white gap-3 px-3 py-2.5 transition hover:bg-secondary-50/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-secondary-100"
      >
        {content}
      </a>
    </li>
  );
}

/**
 * Curated Project Files list that opens Egnyte folders in a new tab.
 *
 * @returns {JSX.Element}
 */
const ProjectFilesFolders = () => {
  const { documentFolders, placeholderContent } = useProjectPageContext();
  const { documents } = placeholderContent;
  const {
    folders,
    webUrl,
    isLoading,
    statsLoading,
    error,
  } = documentFolders;

  return (
    <section className="rounded-xl border border-gray-100 bg-gray-100 px-3 py-2 shadow-sm shadow-gray-300">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <ProjectSectionTitle>{documents.filesTitle}</ProjectSectionTitle>
          <p className="mt-0.5 text-xs font-medium text-gray-500">
            {documents.filesSubtitle}
          </p>
        </div>
        {webUrl ? (
          <a
            href={webUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 text-xs font-semibold text-gray-800 transition hover:border-secondary-200 hover:bg-secondary-50 hover:text-secondary-800"
          >
            Open project folder
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
          {error}
        </div>
      ) : null}

      {isLoading && !folders.length ? (
        <div className="px-1 py-4 text-sm font-medium text-gray-500">
          Resolving project folder…
        </div>
      ) : null}

      {folders.length ? (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
          {folders.map((folder) => (
            <ProjectFileFolderRow
              key={folder.name}
              folder={folder}
              statsLoading={statsLoading}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default ProjectFilesFolders;
